import type { AxiosError } from 'axios';
import type { Query, QueryKey, UseQueryOptions, WithRequired } from '@tanstack/react-query';

declare const dataTagSymbol: unique symbol;

// v5 mirror, with `queryOptions` below: the key carries the type its queryFn returns, so a cache read by that key
// needs no type written in. Type only — the key is the same array at run time.
export type DataTag<TQueryKey extends QueryKey, TData> = TQueryKey & { [dataTagSymbol]: TData };

declare module '@tanstack/query-core' {
  interface QueryClient {
    // v5 mirror: a key from `queryOptions` gives the read its type. Writes keep the explicit generic until v5 —
    // v4's own `setQueryData` overload accepts any updater, so a tagged overload for it would check nothing.
    getQueryData<TTaggedKey extends DataTag<QueryKey, unknown>>(
      queryKey: TTaggedKey,
    ): TTaggedKey[typeof dataTagSymbol] | undefined;
  }
}

type V5QueryOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = WithRequired<
  Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'onSuccess' | 'onSettled' | 'refetchInterval'>,
  'queryKey'
> & {
  refetchInterval?:
    | number
    | false
    | ((data: unknown, query: Query<TQueryFnData, TError, TQueryFnData, TQueryKey>) => number | false);
};

/**
 * Local mirror of v5's `queryOptions()` helper.
 *
 * Purpose: catch v5-only option names (`gcTime`, etc.) at compile time via
 * excess-property check on the literal. The check only fires because the
 * parameter is typed as a fixed options shape, not as a generic
 * constraint — generic constraints don't trigger excess-property checks.
 *
 * The shape drops v4 options that receive the data after `select`: `onSuccess`,
 * `onSettled` and `refetchInterval(data)`. Kept in the factory's type, they fix
 * `TData` to the queryFn result, and `useQuery({ ...factory(), select })` with a
 * `select` that changes the type fails to compile. v5 removed the two callbacks and
 * passes the query to `refetchInterval`; here a polling factory reads
 * `query.state.data` from the second argument (v4 passes the selected data first).
 *
 * On the v4 → v5 migration:
 *   - import from '@tanstack/react-query' instead of '@/services/query';
 *   - `refetchInterval: (_, query) => …` becomes `refetchInterval: (query) => …`;
 *   - `onError` (still accepted here, it does not depend on `TData`) moves out of the
 *     factories: v5 removed it from queries.
 *   - delete `DataTag` and the `QueryClient` augmentation: v5 tags the key itself, and the
 *     reads (`qc.getQueryData(factory().queryKey)`) stay as they are.
 *
 * `TError` defaults to `AxiosError` to align with `useApiMutation`.
 * `queryKey` is required, as in v5: v4 marks it optional only for the positional `useQuery(key, fn)` form.
 */
export function queryOptions<
  TQueryFnData,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: V5QueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): V5QueryOptions<TQueryFnData, TError, TData, DataTag<TQueryKey, TQueryFnData>>;
export function queryOptions(options: unknown) {
  return options;
}
